import React, { useState, useEffect } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { logInteraction } from '../utils/tracker.js';
import DOMPurify from 'dompurify';
import '../styles.css';
import { Helmet } from 'react-helmet-async';
import { getMetadataBundle } from '../metadata/languageMeta.js';
import { getMetadataBundleForStaticPage } from '../metadata/SEOmetadata.js';
import { t } from '../i18n/translationStrings.js';

const S3_BUCKET_URL = process.env.REACT_APP_S3_BUCKET_URL;
const PLACEHOLDER_COVER = `${window.location.origin}/images/PlaceholderJPG.webp`;

const languageMap = {
  en: 'English',
  de: 'German',
  fr: 'French',
  es: 'Spanish',
  it: 'Italian',
  pt: 'Portuguese',
  zh: 'Chinese',
  ru: 'Russian',
  sv: 'Swedish',
  da: 'Danish',
  pl: 'Polish',
  fi: 'Finnish',
};

const SearchPage = () => {
  const [query, setQuery] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const resultsPerPage = 30;

  const location = useLocation();
  const [, setSearchParams] = useSearchParams(); // we only need the setter

  // React to URL changes (query string) and kick off a search
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchQuery = params.get('query') || '';
    const searchLanguage = params.get('language') || '';

    setQuery(searchQuery);
    setSelectedLanguage(searchLanguage);

    if (searchQuery) {
      searchBooks(searchQuery, searchLanguage);
    } else {
      setResults([]);
    }
	
  }, [location.search]);

  const fetchMetadata = async () => {
    try {
      const bucketResponse = await fetch(`${S3_BUCKET_URL}/HermesTest/metadata.json`);
      const bucketBooks = await bucketResponse.json();
      const bucketBooksWithSource = bucketBooks.map((book) => ({ ...book, source: 's3' }));

      const publicResponse = await fetch('/ProductMetaData.json');
      const publicBooks = await publicResponse.json();
      const publicBooksWithSource = publicBooks.map((book) => ({ ...book, source: 'public' }));

      const allBooks = [...bucketBooksWithSource, ...publicBooksWithSource];

      // Add titleSlug fallback if missing
      allBooks.forEach((book) => {
        if (!book.titleSlug) {
          book.titleSlug = book.title
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
        }
      });

      return allBooks;
    } catch (error) {
      console.error('Error fetching metadata:', error);
      return [];
    }
  };

  const sanitizeInput = (input) => {
    return DOMPurify.sanitize(input.trim(), { ALLOWED_TAGS: [] });
  };

  const searchBooks = async (searchQuery, language) => {
    const sanitizedQuery = sanitizeInput(searchQuery);
    if (!sanitizedQuery) return;
    setLoading(true);

    try {
      const metadata = await fetchMetadata();
      const terms = sanitizedQuery.toLowerCase().split(/[\s,]+/); // Split by space or comma

      const filtered = metadata.filter((book) => {
        const matchesAllTerms = terms.every((term) => {
          return (
            book.title.toLowerCase().includes(term) ||
            book.author.toLowerCase().includes(term) ||
            (book.categories || []).some((cat) => cat.toLowerCase().includes(term))
          );
        });
        const languageMatch = language ? book.language === language : true;

        return matchesAllTerms && languageMatch;
      });

      setResults(filtered);
      setCurrentPage(1);

      logInteraction('search', { query: sanitizedQuery, language });
    } catch (error) {
      console.error('Error fetching book data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();

    // Update URL via React Router (not window.history)
    const next = {};
    if (query) next.query = query;
    if (selectedLanguage) next.language = selectedLanguage;

    // This updates the query string and notifies the router
    setSearchParams(next, { replace: true });
    // No need to call searchBooks here; the effect on location.search will run it.
  };

  const truncateText = (text, maxLength) => {
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
  };

  const indexOfLastResult = currentPage * resultsPerPage;
  const indexOfFirstResult = indexOfLastResult - resultsPerPage;
  const currentResults = results.slice(indexOfFirstResult, indexOfLastResult);
  const totalPages = Math.ceil(results.length / resultsPerPage);

  const pageMetadata = getMetadataBundleForStaticPage('search');
  const metadata = getMetadataBundle('search', results[0]?.language || 'en', query, {
    url: `https://www.hermesreads.com${location.pathname}${location.search}`,
    image: pageMetadata.og?.image || '/images/PlaceholderJPG.webp',
  });

  return (
    <main className="search-page" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Helmet>
        <html lang={metadata.htmlLang} />
        <title>{metadata.title}</title>
        <meta name="description" content={metadata.description} />
        <meta name="keywords" content={metadata.keywords} />

        <meta property="og:title" content={metadata.og.title} />
        <meta property="og:description" content={metadata.og.description} />
        <meta property="og:image" content={metadata.og.image} />
        <meta property="og:type" content={metadata.og.type} />
        <meta property="og:url" content={metadata.og.url} />

        <meta name="twitter:card" content={metadata.twitter.card} />
        <meta name="twitter:title" content={metadata.twitter.title} />
        <meta name="twitter:description" content={metadata.twitter.description} />
        <meta name="twitter:image" content={metadata.twitter.image} />

        <link rel="canonical" href={metadata.og.url} />
        <script type="application/ld+json">{JSON.stringify(metadata.structuredData)}</script>
      </Helmet>

      {/* Search Form with Language Selection */}
      <form
        onSubmit={handleSearch}
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '1rem',
          gap: '0.5rem',
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        <select
          className="language-dropdown"
          value={selectedLanguage}
          onChange={(e) => setSelectedLanguage(e.target.value)}
          style={{
            padding: '0.5em 0.7em',
            fontSize: '0.9em',
            borderRadius: '4px',
            border: '1px solid #444',
            backgroundColor: 'var(--light-gray)',
            color: 'var(--text-color)',
          }}
        >
          <option value="">{t('languageLabel')}</option>
          {Object.entries(languageMap).map(([code, name]) => (
            <option key={code} value={code}>
              {name}
            </option>
          ))}
        </select>

        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by title, author, or category..."
          style={{
            padding: '0.5em 0.7em',
            fontSize: '0.9em',
            borderRadius: '4px',
            border: '1px solid #444',
            backgroundColor: 'var(--light-gray)',
            color: 'var(--text-color)',
            width: '250px',
          }}
        />

        <button
          type="submit"
          style={{
            padding: '0.5em 1em',
            fontSize: '0.9em',
            backgroundColor: 'var(--primary-color)',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          {t('searchButton')}
        </button>
      </form>

      <section className="results-container" style={{ width: '100%', maxWidth: '1000px' }}>
        <h2 style={{ textAlign: 'center' }}>
          {t('searchResults')} {t('for') || 'for'} "{DOMPurify.sanitize(query)}"
        </h2>

        {loading && (
          <div id="loading" className="loading" style={{ textAlign: 'center' }}>
            {t('loading')}
          </div>
        )}

        <div className="results-grid">
          {currentResults.map((book) => {
            const sanitizedTitle = DOMPurify.sanitize(book.title);
            const sanitizedAuthor = DOMPurify.sanitize(book.author);
            const sanitizedCategories = book.categories ? DOMPurify.sanitize(book.categories.join(', ')) : '';
            const languageFullName = languageMap[book.language] || book.language;

            return (
              <div className="book-card" key={book.id}>
                <Link
                  to={book.source === 'public' ? '#' : `/book/${book.titleSlug || book.id}`}
                  className="book-link"
                >
                  <div
                    className="book-cover"
                    style={{
                      backgroundImage: `url(${book.cover ? book.cover : PLACEHOLDER_COVER})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  />
                  <div className="book-info">
                    <h3 className="book-title" style={{ color: 'var(--highlight-color)' }}>
                      {truncateText(sanitizedTitle, 42)}
                    </h3>
                    <p className="book-author" style={{ color: 'var(--primary-color)' }}>
                      By {truncateText(sanitizedAuthor, 22)}
                    </p>
                    <p style={{ color: 'var(--text-color)' }}>Language: {languageFullName}</p>
                  </div>
                </Link>
              </div>
            );
          })}
          {currentResults.length === 0 && !loading && <p style={{ textAlign: 'center' }}>No results found.</p>}
        </div>

        {/* (Optional) simple pager if you want to keep it visible */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', marginTop: '1rem' }}>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <span>
              {currentPage} / {totalPages}
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        )}
      </section>
    </main>
  );
};

export default SearchPage;
